// The pop-up "New chat", "New group" and "Group info" open in: the search pop-up's look
// (components/shell/PageSearch): the glass panel of index.css over a lightly dimmed,
// blurred page.
//
// Escape, the close button and a click on the page behind all close it, except while
// the caller says it is busy (a group being made or changed): then they wait for the
// answer, so nothing the person asked for finishes out of sight. A field inside that
// uses Escape itself (stopping a rename) says so with preventDefault, and the pop-up
// stays. Focus goes to the field the caller names when it opens, and Tab stays inside
// it while it is open. Where focus goes when it closes is the caller's (the New button,
// the conversation opened, the Group info button).
import { useEffect, useId, useRef } from 'react';
import type { KeyboardEvent, ReactElement, ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface MessagesDialogProps {
  title: string;
  onClose: () => void;
  /** The field that has the keyboard when the pop-up opens. */
  initialFocus: RefObject<HTMLElement | null>;
  /** Something is on its way that must be answered here: the pop-up does not close until it is. */
  busy?: boolean;
  children: ReactNode;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function MessagesDialog({ title, onClose, initialFocus, busy = false, children }: MessagesDialogProps): ReactElement {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  useEffect(() => { close.current = onClose; }, [onClose]);
  const busyNow = useRef(busy);
  useEffect(() => { busyNow.current = busy; }, [busy]);
  const tryClose = () => { if (!busyNow.current) close.current(); };

  useEffect(() => {
    initialFocus.current?.focus();
    // The page behind must not scroll while the pop-up is up.
    const before = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = before; };
    // Once, on opening: the field is the same for as long as the pop-up is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On the document, not the panel: after a click on the dimmed page, focus is outside it.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape' || e.isComposing || e.defaultPrevented) return;
      e.preventDefault();
      if (!busyNow.current) close.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Tab goes round the pop-up's own controls and never out to the page behind.
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !panel.current) return;
    const stops = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    // Everything waits (a group being made): Tab goes nowhere, not out to the page.
    if (stops.length === 0) {
      e.preventDefault();
      return;
    }
    const first = stops[0];
    const last = stops[stops.length - 1];
    const at = document.activeElement;
    if (e.shiftKey && (at === first || !panel.current.contains(at))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (at === last || !panel.current.contains(at))) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-16 sm:pt-[12vh]">
      {/* A light dim and a blur: the page stays visible behind the glass, out of focus. */}
      <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" onClick={tryClose} aria-hidden="true" />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
        className="glass-panel relative flex max-h-[calc(100dvh-5rem)] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white/90 sm:max-h-[76vh]"
      >
        <div className="flex flex-none items-center justify-between gap-3 px-5 pb-2 pt-4">
          <h2 id={titleId} className="text-base font-semibold text-gray-900">{title}</h2>
          <button
            type="button"
            onClick={tryClose}
            disabled={busy}
            aria-label="Close"
            className="-mr-1.5 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
